import { ComponentRef, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, BaseRouteReuseStrategy, DetachedRouteHandle } from '@angular/router';

/**
 * Keeps each Playground endpoint page alive while you switch between them,
 * so /rag's form and last response are still there after a look at /answer
 * (and a run in flight finishes in the background). Only the pages under
 * /playground are kept, one per endpoint, and only while the Playground is
 * open: it calls clear() when it's destroyed, so leaving it, a reload or a
 * logout starts every page fresh. Everything else routes as usual.
 */
@Injectable()
export class PlaygroundReuseStrategy extends BaseRouteReuseStrategy {
  private readonly stored = new Map<string, DetachedRouteHandle>();

  override shouldDetach(route: ActivatedRouteSnapshot): boolean {
    return !!keyOf(route);
  }

  override store(route: ActivatedRouteSnapshot, handle: DetachedRouteHandle | null): void {
    const key = keyOf(route);
    if (!key) return;
    if (handle) this.stored.set(key, handle);
    else this.stored.delete(key);
  }

  override shouldAttach(route: ActivatedRouteSnapshot): boolean {
    const key = keyOf(route);
    return !!key && this.stored.has(key);
  }

  override retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
    const key = keyOf(route);
    return key ? this.stored.get(key) ?? null : null;
  }

  /** Destroys the kept pages (the router won't: a stored page is outside
   *  any outlet). */
  clear(): void {
    for (const handle of this.stored.values()) {
      (handle as { componentRef?: ComponentRef<unknown> }).componentRef?.destroy();
    }
    this.stored.clear();
  }
}

/** "rag", "answer"… for an endpoint page under /playground; null otherwise. */
function keyOf(route: ActivatedRouteSnapshot): string | null {
  const path = route.routeConfig?.path;
  return route.routeConfig?.component && path && route.parent?.routeConfig?.path === 'playground' ? path : null;
}
